import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { MessageSquare, Clock, CheckCircle, XCircle, Loader2, Trash2, ChevronDown, ChevronUp, Mail, User, Calendar, Tag } from 'lucide-react';

const SupportRequestsManager = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editingNotes, setEditingNotes] = useState({});
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/support-requests`);
      setRequests(response.data.support_requests || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Only super admin can view support requests');
      } else {
        toast.error('Failed to load support requests');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/admin/support-requests/${requestId}`, {
        status: newStatus
      });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSaveNotes = async (requestId) => {
    try {
      await axios.put(`${API}/admin/support-requests/${requestId}`, {
        admin_notes: editingNotes[requestId]
      });
      toast.success('Notes saved');
      setEditingNotes({ ...editingNotes, [requestId]: undefined });
      fetchRequests();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this support request? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/support-requests/${requestId}`);
      toast.success('Support request deleted');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      in_progress: { label: 'In Progress', className: 'bg-blue-100 text-blue-800 border-blue-300' },
      resolved: { label: 'Resolved', className: 'bg-green-100 text-green-800 border-green-300' },
      closed: { label: 'Closed', className: 'bg-gray-100 text-gray-800 border-gray-300' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  const getCategoryBadge = (category) => {
    const categoryColors = {
      'General': 'bg-gray-100 text-gray-700',
      'Technical': 'bg-purple-100 text-purple-700',
      'Billing': 'bg-green-100 text-green-700',
      'Assessment': 'bg-blue-100 text-blue-700',
      'Organization': 'bg-orange-100 text-orange-700',
      'Feedback': 'bg-pink-100 text-pink-700',
      'Other': 'bg-slate-100 text-slate-700'
    };
    return (
      <Badge variant="secondary" className={categoryColors[category] || categoryColors['Other']}>
        {category}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRequests = requests.filter(req => {
    if (filter === 'all') return true;
    return req.status === filter;
  });

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    in_progress: requests.filter(r => r.status === 'in_progress').length,
    resolved: requests.filter(r => r.status === 'resolved').length
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading support requests...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Support Requests
              </CardTitle>
              <CardDescription>
                Manage and respond to user support requests
              </CardDescription>
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({stats.total})</SelectItem>
                <SelectItem value="pending">Pending ({stats.pending})</SelectItem>
                <SelectItem value="in_progress">In Progress ({stats.in_progress})</SelectItem>
                <SelectItem value="resolved">Resolved ({stats.resolved})</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>

        {/* Stats Row */}
        <CardContent className="border-t bg-gray-50 py-4">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
              <div className="text-sm text-gray-500">Total</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
              <div className="text-sm text-gray-500">Pending</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{stats.in_progress}</div>
              <div className="text-sm text-gray-500">In Progress</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{stats.resolved}</div>
              <div className="text-sm text-gray-500">Resolved</div>
            </div>
          </div>
        </CardContent>

        <CardContent className="p-0">
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No support requests found</p>
              {filter !== 'all' && (
                <Button variant="link" onClick={() => setFilter('all')} className="mt-2">
                  View all requests
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {filteredRequests.map((request) => (
                <div key={request.id} className="p-4 hover:bg-gray-50 transition-colors">
                  {/* Request Header */}
                  <div 
                    className="flex items-start justify-between cursor-pointer"
                    onClick={() => setExpandedId(expandedId === request.id ? null : request.id)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusBadge(request.status)}
                        {getCategoryBadge(request.category)}
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(request.created_at)}
                        </span>
                      </div>
                      <h4 className="font-semibold text-gray-800">{request.subject}</h4>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {request.name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          <a href={`mailto:${request.email}`} className="text-indigo-600 hover:underline" onClick={(e) => e.stopPropagation()}>
                            {request.email}
                          </a>
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(request.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      {expandedId === request.id ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedId === request.id && (
                    <div className="mt-4 pt-4 border-t space-y-4">
                      {/* Message */}
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Message:</h5>
                        <div className="bg-white p-4 rounded-lg border text-gray-700 whitespace-pre-wrap">
                          {request.message}
                        </div>
                      </div>

                      {/* Status Update */}
                      <div className="flex items-center gap-4">
                        <span className="text-sm font-medium text-gray-700">Update Status:</span>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={request.status === 'pending' ? 'default' : 'outline'}
                            className={request.status === 'pending' ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
                            onClick={() => handleStatusChange(request.id, 'pending')}
                          >
                            <Clock className="h-3 w-3 mr-1" />
                            Pending
                          </Button>
                          <Button
                            size="sm"
                            variant={request.status === 'in_progress' ? 'default' : 'outline'}
                            className={request.status === 'in_progress' ? 'bg-blue-500 hover:bg-blue-600' : ''}
                            onClick={() => handleStatusChange(request.id, 'in_progress')}
                          >
                            <Loader2 className="h-3 w-3 mr-1" />
                            In Progress
                          </Button>
                          <Button
                            size="sm"
                            variant={request.status === 'resolved' ? 'default' : 'outline'}
                            className={request.status === 'resolved' ? 'bg-green-500 hover:bg-green-600' : ''}
                            onClick={() => handleStatusChange(request.id, 'resolved')}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Resolved
                          </Button>
                          <Button
                            size="sm"
                            variant={request.status === 'closed' ? 'default' : 'outline'}
                            className={request.status === 'closed' ? 'bg-gray-500 hover:bg-gray-600' : ''}
                            onClick={() => handleStatusChange(request.id, 'closed')}
                          >
                            <XCircle className="h-3 w-3 mr-1" />
                            Closed
                          </Button>
                        </div>
                      </div>

                      {/* Admin Notes */}
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Admin Notes:</h5>
                        <Textarea
                          placeholder="Add internal notes about this request..."
                          rows={3}
                          value={editingNotes[request.id] !== undefined ? editingNotes[request.id] : (request.admin_notes || '')}
                          onChange={(e) => setEditingNotes({ ...editingNotes, [request.id]: e.target.value })}
                          className="bg-white"
                        />
                        {editingNotes[request.id] !== undefined && editingNotes[request.id] !== request.admin_notes && (
                          <div className="flex gap-2 mt-2">
                            <Button size="sm" onClick={() => handleSaveNotes(request.id)}>
                              Save Notes
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setEditingNotes({ ...editingNotes, [request.id]: undefined })}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Quick Reply */}
                      <div className="pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                          onClick={() => window.open(`mailto:${request.email}?subject=Re: ${request.subject}`, '_blank')}
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Reply via Email
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SupportRequestsManager;
